import { FormEvent, useState } from "react";
import { useMe } from "../api/hooks/useMe";
import { useUpdateProfile } from "./useUpdateProfile";
import { usePasswordChange } from "./usePasswordChange";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";
import { PageHeader } from "../ui/PageHeader";
import { pickFieldError } from "../lib/form";
import { useTranslation } from "../i18n";
import { SecuritySection } from "./security/SecuritySection";

function Card({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
      <h2 className="mb-4 text-lg font-semibold text-slate-100">{title}</h2>
      {children}
    </section>
  );
}

export function AccountPage() {
  const { t } = useTranslation("account");
  const me = useMe();

  // ── Profile card ─────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState<string>("");
  const [profileInitialized, setProfileInitialized] = useState(false);
  const updateProfile = useUpdateProfile();

  // Seed the display name field once me loads
  if (me.data && !profileInitialized) {
    setDisplayName(me.data.displayName);
    setProfileInitialized(true);
  }

  function onSaveProfile(e: FormEvent): void {
    e.preventDefault();
    updateProfile.mutate({ displayName });
  }

  // ── Password card ─────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmError, setConfirmError] = useState<string | undefined>();
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const changePassword = usePasswordChange();

  function onChangePassword(e: FormEvent): void {
    e.preventDefault();
    setConfirmError(undefined);
    if (newPassword !== confirmPassword) {
      setConfirmError(t("password.mismatch"));
      return;
    }
    setPasswordSuccess(false);
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setPasswordSuccess(true);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        },
      },
    );
  }

  const profileNameError = pickFieldError(updateProfile.error, "displayName");
  const profileGenericError =
    updateProfile.error && updateProfile.error.code !== "validation_failed"
      ? updateProfile.error.message
      : undefined;

  const pwCurrentError = pickFieldError(changePassword.error, "currentPassword");
  const pwNewError = pickFieldError(changePassword.error, "newPassword");
  const pwGenericError =
    changePassword.error && changePassword.error.code !== "validation_failed"
      ? changePassword.error.message
      : undefined;

  if (me.isLoading) {
    return (
      <div className="flex items-center justify-center p-16"><Spinner /></div>
    );
  }

  if (!me.data) return null;

  return (
    <div className="mx-auto max-w-xl p-8 [&>section]:mt-6">
      <PageHeader title={t("page.title")} description={t("page.description")} />

      {/* Profile card */}
      <Card title={t("profile.title")}>
        {profileGenericError ? (
          <Banner kind="error" className="mb-4">{profileGenericError}</Banner>
        ) : null}
        {updateProfile.isSuccess ? (
          <Banner kind="success" className="mb-4">{t("profile.updated")}</Banner>
        ) : null}

        <form onSubmit={onSaveProfile} className="space-y-4" noValidate>
          {/* Email is read-only */}
          <Field label={t("profile.emailLabel")}>
            {({ id }) => (
              <TextInput
                id={id}
                type="email"
                value={me.data!.email}
                readOnly
                disabled
                className="cursor-not-allowed opacity-60"
              />
            )}
          </Field>

          <Field label={t("profile.displayNameLabel")} error={profileNameError}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                type="text"
                autoComplete="name"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                hasError={!!profileNameError}
                {...ariaProps}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            disabled={updateProfile.isPending}
          >
            {updateProfile.isPending ? t("profile.saving") : t("profile.save")}
          </Button>
        </form>
      </Card>

      {/* Password card */}
      <Card title={t("password.title")}>
        {pwGenericError ? (
          <Banner kind="error" className="mb-4">{pwGenericError}</Banner>
        ) : null}
        {passwordSuccess ? (
          <Banner kind="success" className="mb-4">{t("password.updated")}</Banner>
        ) : null}

        <form onSubmit={onChangePassword} className="space-y-4" noValidate>
          <Field label={t("password.currentLabel")} error={pwCurrentError}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                hasError={!!pwCurrentError}
                {...ariaProps}
              />
            )}
          </Field>

          <Field label={t("password.newLabel")} error={pwNewError}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                hasError={!!pwNewError}
                {...ariaProps}
              />
            )}
          </Field>

          <Field label={t("password.confirmLabel")} error={confirmError}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                hasError={!!confirmError}
                {...ariaProps}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            disabled={changePassword.isPending}
          >
            {changePassword.isPending ? t("password.updating") : t("password.submit")}
          </Button>
        </form>
      </Card>

      {/* Security: two-factor + passkeys */}
      <SecuritySection />
    </div>
  );
}
